import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { obtenerMenusBloqueados } from '@/hooks/useMenusBloqueados';

interface PagePermissions {
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canApprove: boolean;
  canExport: boolean;
  /** Permiso 8 `generar_oferta`: el flujo de PDF. Hoy apagado en base, vivo en codigo. */
  canGenerateOffer: boolean;
  /** Permiso 9 `generar_oferta_digital`: el flujo vigente. */
  canGenerateDigitalOffer: boolean;
  /** Permiso `cambiar_correo`: cambiar el correo de acceso desde esta pantalla. */
  canChangeEmail: boolean;
  /** Permiso `devolver_pago`: marcar un pago como devuelto al cliente. */
  canReturnPayment: boolean;
  /** Permiso `consolidar_pago`: consolidar la evidencia de un pago (también con el estado de cuenta). */
  canConsolidatePayment: boolean;
  /** Permiso `registrar_pago`: registrar un pago a mano y cargar la evidencia de un pago (detalle de cuenta de cobranza). */
  canRegisterPayment: boolean;
  /** Permiso `transferir_pago`: mover un pago de esta cuenta a otra. */
  canTransferPayment: boolean;
  /** Permiso `editar_cuenta`: abrir "Editar cuenta" (datos, compradores, plan) en el detalle de cuenta. */
  canEditAccount: boolean;
  /** Permiso `reiniciar_financiamiento`: reiniciar la forma de pago / financiamiento de la propiedad. */
  canResetFinancing: boolean;
  /** Permiso `ajustar_plan_pagos`: reconciliar acuerdos contra el precio, cuadrar centavos y recalcular la dispersión. */
  canAdjustPaymentPlan: boolean;
  /** Permiso `gestionar_multas`: agregar, editar y quitar multas de un acuerdo. */
  canManageFines: boolean;
  /** Permiso `generar_estado_cuenta`: generar y ver el estado de cuenta en PDF. */
  canGenerateAccountStatement: boolean;
  /** Permiso `subir_documento_cuenta`: subir documentos a la cuenta (y al expediente del comprador desde la cuenta). */
  canUploadAccountDocument: boolean;
  /** Permiso `marcar_estatus_especial`: marcar o quitar Rescisión (Extraordinario en Productos) en una cuenta de cobranza. */
  canMarkSpecialStatus: boolean;
  /** Permiso `integrar_pago_final`: integrar las mensualidades pendientes de una cuenta a su pago final, o deshacerlo. */
  canIntegrateFinalPayment: boolean;
  /** Permiso `escritura_y_entrega`: iniciar o marcar una entrega sin escritura (escritura y entrega en el mismo acto). */
  canEscrituraYEntrega: boolean;
  /** Permiso `conectar_telefono_notificaciones`: monitor de WhatsApp del header y QR para reconectar (Configuración WhatsApp). */
  canConnectNotificationPhone: boolean;
  /** Permiso `configurar_solo_apps`: marcar un pipeline de tickets como buzón de app (Pipelines). */
  canConfigureSoloApps: boolean;
  /** Permiso `derivar_a_banco`: derivar una solicitud del Broker Bancario a un banco (submenú del broker). */
  canDeriveToBank: boolean;
  /** Permiso `absorber_saldo`: registrar un ajuste absorbido por SOZU que liquida el saldo de una cuenta escriturada. */
  canAbsorbBalance: boolean;
  /** Permiso `registrar_dacion_en_pago`: registrar la dación en pago de una unidad Asignada y corregir su evidencia y justificación. */
  canDacionEnPago: boolean;
  /** Permiso `corregir_validacion_contrato`: corregir el precio y la fecha validados de un contrato (Validación de contratos). */
  canCorrectContractValidation: boolean;
  /** Permiso `dispersar`: ejecutar la dispersión de comisiones por STP (Bandeja de ejecución). */
  canDisperse: boolean;
  /** Permiso `editar_cuenta_cerrada`: registrar y editar pagos (y editar la cuenta) aunque la propiedad ya esté entregada (estatus 8). */
  canEditClosedAccount: boolean;
  /** Permiso `aprobar_excepcion_evidencia`: aprobar o revocar por excepción la evidencia de un pago (p. ej. de una sola página). */
  canApproveEvidenceException: boolean;
  /** Permiso `gestionar_contacto_chat`: ver los chats del cliente y la ficha de su contacto del CRM desde esta pantalla. */
  canManageContactChat: boolean;
  /** Permiso `ver_todos`: ver la pestaña "Todos" de la pantalla (unión de sus pestañas); sin él, solo las de trabajo. */
  canSeeAll: boolean;
}

const DEFAULT_PERMISSIONS: PagePermissions = {
  canRead: false,
  canCreate: false,
  canUpdate: false,
  canDelete: false,
  canApprove: false,
  canExport: false,
  canGenerateOffer: false,
  canGenerateDigitalOffer: false,
  canChangeEmail: false,
  canReturnPayment: false,
  canConsolidatePayment: false,
  canRegisterPayment: false,
  canTransferPayment: false,
  canEditAccount: false,
  canResetFinancing: false,
  canAdjustPaymentPlan: false,
  canManageFines: false,
  canGenerateAccountStatement: false,
  canUploadAccountDocument: false,
  canMarkSpecialStatus: false,
  canIntegrateFinalPayment: false,
  canEscrituraYEntrega: false,
  canConnectNotificationPhone: false,
  canConfigureSoloApps: false,
  canDeriveToBank: false,
  canAbsorbBalance: false,
  canDacionEnPago: false,
  canCorrectContractValidation: false,
  canDisperse: false,
  canEditClosedAccount: false,
  canApproveEvidenceException: false,
  canManageContactChat: false,
  canSeeAll: false,
};

const SUPER_ADMIN_PERMISSIONS: PagePermissions = {
  canRead: true,
  canCreate: true,
  canUpdate: true,
  canDelete: true,
  canApprove: true,
  canExport: true,
  canGenerateOffer: true,
  canGenerateDigitalOffer: true,
  canChangeEmail: true,
  canReturnPayment: true,
  canConsolidatePayment: true,
  canRegisterPayment: true,
  canTransferPayment: true,
  canEditAccount: true,
  canResetFinancing: true,
  canAdjustPaymentPlan: true,
  canManageFines: true,
  canGenerateAccountStatement: true,
  canUploadAccountDocument: true,
  canMarkSpecialStatus: true,
  canIntegrateFinalPayment: true,
  canEscrituraYEntrega: true,
  canConnectNotificationPhone: true,
  canConfigureSoloApps: true,
  canDeriveToBank: true,
  canAbsorbBalance: true,
  canDacionEnPago: true,
  canCorrectContractValidation: true,
  canDisperse: true,
  canEditClosedAccount: true,
  canApproveEvidenceException: true,
  canManageContactChat: true,
  canSeeAll: true,
};

/** ¿El permiso está disponible (activo) en el submenú de esta ruta? Falla cerrado. */
async function permisoDisponibleEnSubmenu(pagePath: string, permiso: string): Promise<boolean> {
  if (!pagePath) return false;
  const { data, error } = await supabase
    .from('submenus_permisos_disponibles')
    .select('id, submenus!inner(vista_front_end, activo), permisos!inner(nombre)')
    .eq('submenus.vista_front_end', pagePath)
    .eq('submenus.activo', true)
    .eq('permisos.nombre', permiso)
    .eq('activo', true)
    .limit(1);
  if (error) {
    console.error('Error fetching permiso disponible:', error);
    return false;
  }
  return (data?.length ?? 0) > 0;
}

export function usePagePermissions(pagePath: string) {
  const { profile, permissionVersion } = useAuth();
  const [permissions, setPermissions] = useState<PagePermissions>(DEFAULT_PERMISSIONS);
  const [isLoading, setIsLoading] = useState(true);
  
  // Ref para evitar mostrar spinner en recargas subsecuentes
  const hasLoadedOnce = useRef(false);

  // Super Admin has all permissions
  const isSuperAdmin = profile?.rol_nombre === 'Super Administrador';

  const fetchPermissions = useCallback(async () => {
    if (isSuperAdmin) {
      // Super Admin tiene todo, salvo un permiso apagado en el submenú: generar_oferta (PDF de
      // cotización) está desactivado y no se muestra a nadie, ni a Super Admin, hasta que se
      // vuelva a activar en submenus_permisos_disponibles.
      const pdfDisponible = await permisoDisponibleEnSubmenu(pagePath, 'generar_oferta');
      setPermissions({ ...SUPER_ADMIN_PERMISSIONS, canGenerateOffer: pdfDisponible });
      setIsLoading(false);
      return;
    }

    if (!profile?.rol_id || !pagePath) {
      if (!hasLoadedOnce.current) {
        setPermissions(DEFAULT_PERMISSIONS);
      }
      setIsLoading(false);
      return;
    }

    try {
      // Solo mostrar spinner la primera vez, recargas son silenciosas
      if (!hasLoadedOnce.current) {
        setIsLoading(true);
      }
      // First get the submenu_id for this path
      const { data: submenuData, error: submenuError } = await supabase
        .from('submenus')
        .select('id, menu_id')
        .eq('vista_front_end', pagePath)
        .eq('activo', true)
        .single();

      if (submenuError || !submenuData) {
        console.error('Error fetching submenu:', submenuError);
        // Solo resetear permisos si nunca hemos cargado exitosamente: un error
        // transitorio en un refresh de fondo (ej. al volver a la pestaña) no
        // debe pisar permisos vigentes y desmontar UI que dependa de ellos
        // (ej. NewOfferDialog en Propiedades).
        if (!hasLoadedOnce.current) {
          setPermissions(DEFAULT_PERMISSIONS);
        }
        setIsLoading(false);
        return;
      }

      // Portal individualizado del que esta sesión quedó fuera: no hay permisos
      // que leer. Sin esto la vista se abriría con todos los permisos de su rol
      // a quien escribiera la ruta a mano.
      const bloqueados = await obtenerMenusBloqueados(permissionVersion);
      if (bloqueados.has(Number((submenuData as any).menu_id))) {
        setPermissions(DEFAULT_PERMISSIONS);
        hasLoadedOnce.current = true;
        setIsLoading(false);
        return;
      }

      // Get all permissions for this submenu and role
      const { data: permissionsData, error: permissionsError } = await supabase
        .from('submenus_permisos')
        .select(`
          permisos!inner (
            nombre
          )
        `)
        .eq('submenu_id', submenuData.id)
        .eq('rol_id', profile.rol_id)
        .eq('activo', true);

      if (permissionsError) {
        console.error('Error fetching permissions:', permissionsError);
        if (!hasLoadedOnce.current) {
          setPermissions(DEFAULT_PERMISSIONS);
        }
        setIsLoading(false);
        return;
      }

      // Map permissions
      const permissionNames = new Set(
        permissionsData?.map((p: any) => p.permisos?.nombre) || []
      );

      setPermissions({
        canRead: permissionNames.has('leer'),
        canCreate: permissionNames.has('crear'),
        canUpdate: permissionNames.has('actualizar'),
        canDelete: permissionNames.has('eliminar'),
        canApprove: permissionNames.has('aprobar'),
        canExport: permissionNames.has('exportar'),
        canGenerateOffer: permissionNames.has('generar_oferta'),
        canGenerateDigitalOffer: permissionNames.has('generar_oferta_digital'),
        canChangeEmail: permissionNames.has('cambiar_correo'),
        canReturnPayment: permissionNames.has('devolver_pago'),
        canConsolidatePayment: permissionNames.has('consolidar_pago'),
        canRegisterPayment: permissionNames.has('registrar_pago'),
        canTransferPayment: permissionNames.has('transferir_pago'),
        canEditAccount: permissionNames.has('editar_cuenta'),
        canResetFinancing: permissionNames.has('reiniciar_financiamiento'),
        canAdjustPaymentPlan: permissionNames.has('ajustar_plan_pagos'),
        canManageFines: permissionNames.has('gestionar_multas'),
        canGenerateAccountStatement: permissionNames.has('generar_estado_cuenta'),
        canUploadAccountDocument: permissionNames.has('subir_documento_cuenta'),
        canMarkSpecialStatus: permissionNames.has('marcar_estatus_especial'),
        canIntegrateFinalPayment: permissionNames.has('integrar_pago_final'),
        canEscrituraYEntrega: permissionNames.has('escritura_y_entrega'),
        canConnectNotificationPhone: permissionNames.has('conectar_telefono_notificaciones'),
        canConfigureSoloApps: permissionNames.has('configurar_solo_apps'),
        canDeriveToBank: permissionNames.has('derivar_a_banco'),
        canAbsorbBalance: permissionNames.has('absorber_saldo'),
        canDacionEnPago: permissionNames.has('registrar_dacion_en_pago'),
        canCorrectContractValidation: permissionNames.has('corregir_validacion_contrato'),
        canDisperse: permissionNames.has('dispersar'),
        canEditClosedAccount: permissionNames.has('editar_cuenta_cerrada'),
        canApproveEvidenceException: permissionNames.has('aprobar_excepcion_evidencia'),
        canManageContactChat: permissionNames.has('gestionar_contacto_chat'),
        canSeeAll: permissionNames.has('ver_todos'),
      });
      hasLoadedOnce.current = true;
    } catch (err) {
      console.error('Error in fetchPermissions:', err);
      // Solo resetear permisos si nunca hemos cargado exitosamente
      if (!hasLoadedOnce.current) {
        setPermissions(DEFAULT_PERMISSIONS);
      }
    } finally {
      setIsLoading(false);
    }
  }, [profile?.rol_id, pagePath, isSuperAdmin, permissionVersion]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions, permissionVersion]);

  return {
    ...permissions,
    isLoading,
    isSuperAdmin,
  };
}
